/**
 * Case-form UI preview: when PAP/Copay deny selects Biosimilar Yes + brand + drug,
 * mirror a Pending row into parent `subform_biosimilar` so staff see it before case Save.
 *
 * - Does not call the API.
 * - Only manages rows tagged `_localBiosimilarPreview` for this create_from.
 * - Never removes persisted biosimilar rows (those with `id`).
 * - Skips adding if any non-deleted biosimilar row already exists (one per case).
 */
import type { Form } from "@formily/core";

const PREVIEW_FLAG = "_localBiosimilarPreview";

function relationID(value: unknown): number | undefined {
	if (value === null || value === undefined) return undefined;
	if (typeof value === "number" && Number.isFinite(value)) return value;
	if (typeof value === "object" && value !== null && "id" in value) {
		const { id } = value as { id?: unknown };
		if (typeof id === "number" && Number.isFinite(id)) return id;
	}
	return undefined;
}

function asRelationValue(value: unknown): { id: number } | undefined {
	const id = relationID(value);
	if (id === undefined) return undefined;
	if (typeof value === "object" && value !== null) {
		return value as { id: number };
	}
	return { id };
}

function syncLocalBiosimilarPreview(opts: {
	form: Form;
	parentForm?: Form | null;
	createFrom: "Patient PAP Form" | "Patient Copay Form";
}): void {
	const { form, parentForm, createFrom } = opts;
	if (!parentForm) return;

	const existing = (parentForm.getFieldState("subform_biosimilar")?.value ??
		[]) as Array<Record<string, unknown>>;

	const withoutOurPreview = existing.filter(
		(row) => !(row[PREVIEW_FLAG] === true && row.create_from === createFrom)
	);

	const hasOtherBiosimilar = withoutOurPreview.some(
		(row) => row.deleted !== true
	);

	const status = form.getFieldState("status")?.value;
	const available = form.getFieldState("is_biosimilar_available")?.value;
	const brandValue = form.getFieldState("biosimilar_brand_offered")?.value;
	const drugValue = form.getFieldState("biosimilar_drug_id")?.value;
	const brand = asRelationValue(brandValue);
	const drug = asRelationValue(drugValue);

	const shouldPreview =
		status === "Denied" &&
		available === "Yes" &&
		brand !== undefined &&
		drug !== undefined &&
		!hasOtherBiosimilar;

	let next = withoutOurPreview;
	if (shouldPreview) {
		const patientID =
			form.getFieldState("patient_id")?.value ??
			parentForm.getFieldState("patient_id")?.value;
		next = [
			...withoutOurPreview,
			{
				_key: `local-biosimilar-${createFrom.replace(/\s+/g, "-")}`,
				[PREVIEW_FLAG]: true,
				status: "Pending",
				create_from: createFrom,
				biosimilar_brand_offered: brand,
				biosimilar_drug_id: drug,
				...(patientID ? { patient_id: patientID } : {}),
			},
		];
	}

	const prevJSON = JSON.stringify(existing);
	const nextJSON = JSON.stringify(next);
	if (prevJSON === nextJSON) return;

	parentForm.setFieldState("subform_biosimilar", (s) => {
		s.value = next;
	});
}

export { PREVIEW_FLAG, syncLocalBiosimilarPreview };
